import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
  Chip,
  createFilterOptions
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, Calculate as CalculateIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { absencesService } from '../../services/absences.service';
import employeesService from '../../services/employees.service';
import { branchesService } from '../../services/branches.service';
import { CreateAbsenceDto, UpdateAbsenceDto, Absence } from '../../types/absences.types';
import { Employee } from '../../types/employees.types';
import { Branch } from '../../types/branches.types';

interface AbsenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  absence?: Absence | null;
}

const filterOptions = createFilterOptions<Employee>({
  matchFrom: 'any',
  stringify: (option: Employee) => `${option.employeeCode} ${option.fullName} ${option.fullNameAr}`,
});

const AbsenceDialog: React.FC<AbsenceDialogProps> = ({ open, onClose, onSave, absence }) => {
  const isRTL = localStorage.getItem('language') === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    branchId: 0,
    employeeId: 0,
    absenceDate: new Date().toISOString().split('T')[0],
    absenceType: 'FullDay',
    days: 1,
    hours: 0,
    reason: '',
    notes: ''
  });

  const [calculatedDeduction, setCalculatedDeduction] = useState<number>(0);

  const t = {
    title: isRTL ? (absence ? 'تعديل غياب' : 'إضافة غياب') : (absence ? 'Edit Absence' : 'Add Absence'),
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    absenceDate: isRTL ? 'تاريخ الغياب' : 'Absence Date',
    absenceType: isRTL ? 'نوع الغياب' : 'Absence Type',
    days: isRTL ? 'عدد الأيام' : 'Days',
    hours: isRTL ? 'عدد الساعات' : 'Hours',
    reason: isRTL ? 'السبب' : 'Reason',
    notes: isRTL ? 'ملاحظات' : 'Notes',
    calculatedDeduction: isRTL ? 'الخصم المحسوب' : 'Calculated Deduction',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    calculate: isRTL ? 'احسب الخصم' : 'Calculate',
    selectBranch: isRTL ? 'اختر الفرع' : 'Select Branch',
    fullDay: isRTL ? 'يوم كامل' : 'Full Day',
    halfDay: isRTL ? 'نصف يوم' : 'Half Day',
    lateHours: isRTL ? 'تأخير (ساعات)' : 'Late Hours',
    excused: isRTL ? 'غياب بعذر' : 'Excused Absence',
    unexcused: isRTL ? 'غياب بدون عذر' : 'Unexcused Absence',
    sickLeave: isRTL ? 'إجازة مرضية' : 'Sick Leave',
    emergencyLeave: isRTL ? 'إجازة طارئة' : 'Emergency Leave',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully'
  };

  useEffect(() => {
    if (open) {
      loadData();
      if (absence) {
        setFormData({
          branchId: absence.branchId,
          employeeId: absence.employeeId,
          absenceDate: absence.absenceDate.split('T')[0],
          absenceType: absence.absenceType,
          days: absence.days,
          hours: absence.hours,
          reason: absence.reason || '',
          notes: absence.notes || ''
        });
        setCalculatedDeduction(absence.deductionAmount);
      } else {
        resetForm();
      }
    }
  }, [open, absence]);

  useEffect(() => {
    if (formData.branchId) {
      setFilteredEmployees(allEmployees.filter(emp => emp.branchId === formData.branchId));
    } else {
      setFilteredEmployees(allEmployees);
    }
  }, [formData.branchId, allEmployees]);

  const loadData = async () => {
    try {
      const [branchesData, employeesData] = await Promise.all([
        branchesService.getAll(),
        employeesService.getAll()
      ]);
      setBranches(branchesData);
      setAllEmployees(employeesData.data || employeesData);
    } catch (error) {
      toast.error('Error loading data');
    }
  };

  const resetForm = () => {
    setFormData({
      branchId: 0,
      employeeId: 0,
      absenceDate: new Date().toISOString().split('T')[0],
      absenceType: 'FullDay',
      days: 1,
      hours: 0,
      reason: '',
      notes: ''
    });
    setCalculatedDeduction(0);
  };

  const calculateDeduction = () => {
    const employee = allEmployees.find(e => e.employeeId === formData.employeeId);
    if (!employee?.basicSalary) {
      toast.error('Please select employee with salary');
      return;
    }

    const basicSalary = employee.basicSalary;
    const dailyRate = basicSalary / 30;
    const hourlyRate = basicSalary / 240;

    let deduction = 0;
    switch (formData.absenceType) {
      case 'FullDay':
      case 'UnexcusedAbsence':
        deduction = dailyRate * formData.days;
        break;
      case 'HalfDay':
        deduction = (dailyRate / 2) * formData.days;
        break;
      case 'LateHours':
        deduction = hourlyRate * formData.hours;
        break;
      default:
        deduction = 0;
    }

    setCalculatedDeduction(parseFloat(deduction.toFixed(3)));
    toast.success(`${t.calculatedDeduction}: ${deduction.toFixed(3)} KD`);
  };

  const handleSave = async () => {
    if (!formData.branchId || !formData.employeeId) {
      toast.error('Branch and Employee are required');
      return;
    }

    try {
      setLoading(true);
      if (absence) {
        await absencesService.update({ ...formData, absenceId: absence.absenceId } as UpdateAbsenceDto);
      } else {
        await absencesService.create(formData as CreateAbsenceDto);
      }
      toast.success(t.saveSuccess);
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving');
    } finally {
      setLoading(false);
    }
  };

  const absenceTypes = [
    { value: 'FullDay', label: t.fullDay },
    { value: 'HalfDay', label: t.halfDay },
    { value: 'LateHours', label: t.lateHours },
    { value: 'ExcusedAbsence', label: t.excused },
    { value: 'UnexcusedAbsence', label: t.unexcused },
    { value: 'SickLeave', label: t.sickLeave },
    { value: 'EmergencyLeave', label: t.emergencyLeave }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'error.dark', color: 'white' }}>
        {t.title}
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
                value={branches.find(b => b.branchId === formData.branchId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, branchId: newValue?.branchId || 0, employeeId: 0 })}
                renderInput={(params) => <TextField {...params} label={t.branch} required />}
                fullWidth
              />
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={filteredEmployees}
                getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
                value={filteredEmployees.find(e => e.employeeId === formData.employeeId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, employeeId: newValue?.employeeId || 0 })}
                renderInput={(params) => <TextField {...params} label={t.employee} required />}
                filterOptions={filterOptions}
                disabled={!formData.branchId}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.absenceDate}
                  value={formData.absenceDate ? new Date(formData.absenceDate) : null}
                  onChange={(date) => setFormData({ ...formData, absenceDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField select fullWidth label={t.absenceType} value={formData.absenceType} onChange={(e) => setFormData({ ...formData, absenceType: e.target.value, days: e.target.value === 'LateHours' ? 0 : 1, hours: e.target.value === 'LateHours' ? 1 : 0 })} required>
                {absenceTypes.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField fullWidth type="number" label={t.days} value={formData.days} onChange={(e) => setFormData({ ...formData, days: parseFloat(e.target.value) || 0 })} disabled={formData.absenceType === 'LateHours'} inputProps={{ min: 0, step: 0.5 }} />
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField fullWidth type="number" label={t.hours} value={formData.hours} onChange={(e) => setFormData({ ...formData, hours: parseFloat(e.target.value) || 0 })} disabled={formData.absenceType !== 'LateHours'} inputProps={{ min: 0, step: 0.5 }} />
            </Box>
          </Box>

          <Box>
            <TextField fullWidth label={t.reason} value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} multiline rows={2} />
          </Box>

          <Box>
            <TextField fullWidth label={t.notes} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} multiline rows={2} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" color="primary" startIcon={<CalculateIcon />} onClick={calculateDeduction} disabled={!formData.employeeId}>{t.calculate}</Button>
            {calculatedDeduction > 0 && <Chip label={`${t.calculatedDeduction}: ${calculatedDeduction.toFixed(3)} KD`} color="error" />}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" color="error" startIcon={<SaveIcon />} disabled={loading}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AbsenceDialog;